import { PropType } from "@vue/composition-api";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { FragmentFields } from "@scrile/api-provider/dist/api/types";
import { $t } from "@/lib/i18nHelpers";

export enum EVENTS {
  STATUS_SHOW = "STATUS_SHOW",
  STATUS_HIDE = "STATUS_HIDE",

  NOTIFICATION_SHOW = "NOTIFICATION_SHOW",
  NOTIFICATION_HIDE = "NOTIFICATION_HIDE",
  NOTIFICATIONS_MARKED_READ = "NOTIFICATIONS_MARKED_READ",
  NOTIFICATIONS_NEW_NOTIFICATION = "NOTIFICATIONS_NEW_NOTIFICATION",

  SHOW_CREATE_POST = "SHOW_CREATE_POST",
  CONTENT_FEED_UPDATED = "CONTENT_FEED_UPDATED",
  CONTENT_FEED_PURCHASE = "CONTENT_FEED_PURCHASE",
  CONTENT_FEED_PURCHASED = "CONTENT_FEED_PURCHASED",
  CONTENT_FEED_PRICE_LOADED = "CONTENT_FEED_PRICE_LOADED",
  CONTENT_FEED_PRICE_CHANGED = "CONTENT_FEED_PRICE_CHANGED",

  SUBSCRIPTION_OFFER_SHOW = "SUBSCRIPTION_OFFER_SHOW",
  MESSAGES_MARK_READ = "MESSAGES_MARK_READ",
  MESSAGES_MARKED_READ = "MESSAGES_MARKED_READ",
  MESSAGES_NEW_MESSAGE = "MESSAGES_NEW_MESSAGE",
  MESSAGES_UPDATE_MESSAGE = "MESSAGES_UPDATE_MESSAGE",
  MESSAGES_UPDATE_THREAD = "MESSAGES_UPDATE_THREAD",
  MESSAGES_LIKED_MESSAGE = "MESSAGES_LIKED_MESSAGE",

  MAIN_MENU_HIDE = "MAIN_MENU_HIDE",
  CALL_OUTGOING = "CALL_OUTGOING",

  BALANCE_REFILL_SHOW = "BALANCE_REFILL_SHOW",
  BALANCE_REFILL_HIDE = "BALANCE_REFILL_HIDE",
  BALANCE_REFILL_PURCHASED = "BALANCE_REFILL_PURCHASED",

  PAYMENT_START_PURCHASE = "PAYMENT_START_PURCHASE",
  PAYMENT_PURCHASED = "PAYMENT_PURCHASED",
  PAYMENT_SUBSCRIBED = "PAYMENT_SUBSCRIBED",

  SHOW_LOGIN_SIGNUP_MODAL = "SHOW_LOGIN_SIGNUP_MODAL",
  SHOW_PROFILE_VERIFYING_CONFIRM = "SHOW_PROFILE_VERIFYING_CONFIRM",
  BLOCKING_STATUS_CHANGED = "BLOCKING_STATUS_CHANGED",

  SUBSCRIPTION_STATUS_CHANGED = "SUBSCRIPTION_STATUS_CHANGED",

  FOLLOWING_STATUS_CHANGE = "FOLLOWING_STATUS_CHANGE",

  UPCOMING_APPOINTMENT_REMINDER = "UPCOMING_APPOINTMENT_REMINDER",

  STREAM_USER_JOIN = "STREAM_USER_JOIN",
  STORIES_SHOW = "STORIES_SHOW",
  ARCHIVED_STORIES_SHOW = "ARCHIVED_STORIES_SHOW",
}

export const DEFAULT_LOCALE = "en-US";

export enum sciIconModePostfix {
  DARK = "dark",
  LIGHT = "light",
}

export enum TagType {
  SYSTEM = "system",
  INFORMATION = "information",
}

export enum ROLES {
  MEMBER = "member",
  CREATOR = "creator",
  ADMIN = "admin",
  MANAGER = "manager",
  TALENT_AGENT = "talent_agent",
}

export enum TIME_MEASURAND {
  D = "days",
  H = "hours",
  M = "minutes",
  S = "seconds",
  MS = "milliseconds",
}

export enum CONTENT_FEED_SORTS {
  NEWEST = "IS_FOLLOWING;PUBLISH_TIME,DESC;SET_ID",
  OLDEST = "IS_FOLLOWING;PUBLISH_TIME,ASC;SET_ID",
  RANDOM = "IS_FOLLOWING;RANDOM,DESC",
}

export const sortFields = [
  {
    title: $t("Newest Posts"),
    slug: "newest",
    sort: CONTENT_FEED_SORTS.NEWEST,
  },
  {
    title: $t("Oldest Posts"),
    slug: "oldest",
    sort: CONTENT_FEED_SORTS.OLDEST,
  },
  {
    title: $t("Random Posts"),
    slug: "random",
    sort: CONTENT_FEED_SORTS.RANDOM,
  },
];

export const REVIEW_COUNT_LIMIT = 3;
export const APPOINTMENT_ONLINE_REMIND_MS = 10 * 60 * 1000;
export const APPOINTMENT_OFFLINE_REMIND_MS = 60 * 60 * 1000;
export const CONTENT_FEED_FIELDS_WITH_REVIEWS_MOCK = "fragment... on ContentFeed { reviews { pageableFragment}}";

export const INPUT_PROPS = {
  label: { type: String, default: "" },
  hideLabel: { type: Boolean, default: false },
  name: { type: String, default: "" },
  hint: { type: String },
  value: { type: String },
  type: { type: String, default: "text" },
  autocomplete: { type: String },
  placeholder: { type: String },
  pattern: { type: String },
  inputmode: { type: String },
  maxlength: { type: String },
  appendIcon: { type: String },
  appendIconSize: { type: String, default: "22" },
  appendIconColor: { type: String },
  prependIcon: { type: String },
  prependIconSize: { type: String, default: "22" },
  prependIconColor: { type: String },
  secondAppendIcon: { type: String },
  secondAppendIconSize: { type: String, default: "22" },
  secondAppendIconColor: { type: String },
  rules: { type: [Array, Object, String] as PropType<string[] | Record<string, any> | string>, default: () => [] },
  rulesMessages: { type: Object as PropType<Record<string, string>>, default: () => ({}) },
  disabled: { type: Boolean, default: false },
  validationMode: {
    type: String,
    validator: (value: string): boolean =>
      ["aggressive", "passive", "lazy", "eager"].includes(value) || value === undefined,
  },
  invalidField: { type: Boolean, default: false },
  hideErrorMessage: { type: Boolean, default: false },
};

export enum connectionStateEnum {
  EMPTY = "",
  CONNECTED = "connected",
  CONNECTING = "connecting",
  DISCONNECTED = "disconnected",
}

export enum ERRORS {
  SPENDING_BALANCE = "{com.scrile.money.validation.SpendingBalanceValidator.message}",
  PAID_CALL_CREATE = "{com.scrile.paid_calls.validation.PaidCallCreateValidator.message}",
  NO_EVENT_TICKET = "{com.scrile.event_posts.security.EventLiveChatSecurityProvider.message}",
  SLOT_IS_NOT_AVAILABLE = "{com.scrile.appointments.validation.AppointmentBookService.slot-not-available.message}",
}

export enum TabType {
  SEPARATOR = "separator",
}

export enum AppointmentLocationItems {
  DUBAI = "Dubai, UAE",
  LONDON = "London, UK",
}

export const paymentMethodNames = {
  balance: $t("Balance"),
  card: $t("Credit card"),
};

export const SUBSCRIPTION_LEVEL_FRAGMENT: FragmentFields<SubscriptionLevel> = [
  "id",
  "userId",
  "pricePackageId",
  { pricePackage: ["amount", { costs: ["cost", "currency"] }] },
  "contentCount",
  "description",
];
